import styled from 'styled-components';

import { useSaveTemplate } from 'modules/templateEditor/hooks/useSaveTemplate';
import { ActionMenu } from 'ui/actionMenu';
import {
  ActionMenuOption,
  AllowedActionMenuElements,
  MENU_ALIGNMENTS,
  MENU_DIRECTIONS,
} from 'ui/actionMenu/types';
import { Button, BUTTON_SIZES, BUTTON_VARIANTS, IconButton } from 'ui/button';
import { ChevronUp24 } from 'ui/icons';
import { borderRadius, spacing } from 'ui/theme';

import { useEditSavedRowDesignContext } from 'modules/templateEditor/context/EditSavedRowDesignContext';
import { useUpdateSavedRowDesign } from 'modules/templateEditor/hooks/useUpdateSavedRowDesign';
import { useEditableFieldsContext } from '../../context/EditableFieldsContext';
import { useQueryParamContext } from '../../context/QueryParamContext';
import { useTemplateQueryContext } from '../../context/TemplateQueryContext';
import { displayContent } from '../content';
import { TemplateEditViews } from '../types';

// expected as button & action menu together
const ComboButton = styled.div`
  display: flex;
  > button:first-child {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
    border-right: ${spacing.single} solid rgb(0 0 0 / 0.08);
  }
  > *:last-child > button {
    border-bottom-left-radius: 0;
    border-top-left-radius: 0;
  }
`;

export const StyledIconButton = styled(IconButton)`
  margin-right: -${spacing.single};
  border-radius: 0 ${borderRadius.md} ${borderRadius.md} 0;
`;

export const SaveButtonCombo = () => {
  const { setView } = useQueryParamContext();
  const { saveTemplate } = useSaveTemplate();
  const { skipUnsavedChangesCheck } = useEditableFieldsContext();
  const { refetch } = useTemplateQueryContext();

  const setViewOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: displayContent.btnSaveDesignAndExit,
      id: 'btn-save-design-and-exit',
      onClick: () =>
        saveTemplate(() => {
          refetch();
          skipUnsavedChangesCheck.current = true;
          setView(TemplateEditViews.settings);
        }),
    },
  ];

  return (
    <ComboButton>
      <Button
        data-test={'btn-save-design'}
        onClick={() => saveTemplate()}
        size={BUTTON_SIZES.LARGE}
        variant={BUTTON_VARIANTS.PRIMARY}
      >
        <span>{displayContent.btnSaveDesign}</span>
      </Button>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        direction={MENU_DIRECTIONS.TOP}
        options={setViewOptions}
      >
        <StyledIconButton
          data-test={'btn-save-design-action-menu'}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          <ChevronUp24 />
        </StyledIconButton>
      </ActionMenu>
    </ComboButton>
  );
};

export default SaveButtonCombo;

export const SaveSavedRowDesignComboButton = () => {
  const { onExitSavedRowDesignEditor } = useEditSavedRowDesignContext();
  const { onSaveRowDesign, isUpdatingSavedRow } = useUpdateSavedRowDesign();

  const setViewOptions: ActionMenuOption[] = [
    {
      as: AllowedActionMenuElements.Button,
      content: 'Save and return to template',
      id: 'btn-save-row-design-and-exit',
      onClick: () =>
        onSaveRowDesign({ onSuccess: onExitSavedRowDesignEditor, showReturnToTemplate: false }),
    },
  ];

  return (
    <ComboButton>
      <Button
        data-test="btn-save-row-design"
        size={BUTTON_SIZES.LARGE}
        variant={BUTTON_VARIANTS.PRIMARY}
        isLoading={isUpdatingSavedRow}
        onClick={() => onSaveRowDesign()}
      >
        <span>Save row</span>
      </Button>
      <ActionMenu
        align={MENU_ALIGNMENTS.RIGHT}
        direction={MENU_DIRECTIONS.TOP}
        options={setViewOptions}
      >
        <StyledIconButton
          data-test={'btn-save-design-action-menu'}
          size={BUTTON_SIZES.LARGE}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          <ChevronUp24 />
        </StyledIconButton>
      </ActionMenu>
    </ComboButton>
  );
};
